import type {XYPosition} from '@xyflow/react';
import {type GenType, type ScreenSize} from '@features/flow/flowService';
import {
  ESTIMATED_COMPONENT_SIZE,
  NODE_SPACING,
  getNodeDimensions,
} from './nodeDimensions';

export type Direction = 'left' | 'right' | 'top' | 'bottom';

type Size = {
  width: number;
  height: number;
};

type ScreenLike = {
  id: string;
  x: number | null;
  y: number | null;
  screenSize: ScreenSize | null;
  type: 'screen' | 'component';
};

type NodeLike = {
  id?: string;
  position?: XYPosition;
  measured?: {
    width?: number;
    height?: number;
  };
  width?: number;
  height?: number;
  data?: {
    screenSize?: ScreenSize;
  };
};

type ScreenBounds = Size & {
  id: string;
  x: number;
  y: number;
  screenSize: ScreenSize | null;
  type: 'screen' | 'component';
};

type ViewportCenterArgs = {
  canvasRect?: DOMRect;
  sidebarRect?: DOMRect;
  screenToFlowPosition: (position: XYPosition) => XYPosition;
  viewportWidth?: number;
  viewportHeight?: number;
};

function roundPosition(position: XYPosition) {
  return {
    x: Math.round(position.x),
    y: Math.round(position.y),
  };
}

function rectsOverlap(
  a: Size & XYPosition,
  b: Size & XYPosition,
  tolerance = 5,
): boolean {
  return !(
    a.x + a.width - tolerance <= b.x ||
    b.x + b.width - tolerance <= a.x ||
    a.y + a.height - tolerance <= b.y ||
    b.y + b.height - tolerance <= a.y
  );
}

function inflateRect(
  rect: Size & XYPosition,
  spacing: number,
): Size & XYPosition {
  return {
    x: rect.x - spacing,
    y: rect.y - spacing,
    width: rect.width + spacing * 2,
    height: rect.height + spacing * 2,
  };
}

function inflateRectInDirection(
  rect: Size & XYPosition,
  spacing: number,
  direction: Direction,
): Size & XYPosition {
  switch (direction) {
    case 'left':
      return {
        x: rect.x - spacing,
        y: rect.y,
        width: rect.width + spacing,
        height: rect.height,
      };
    case 'right':
      return {
        x: rect.x,
        y: rect.y,
        width: rect.width + spacing,
        height: rect.height,
      };
    case 'top':
      return {
        x: rect.x,
        y: rect.y - spacing,
        width: rect.width,
        height: rect.height + spacing,
      };
    case 'bottom':
      return {
        x: rect.x,
        y: rect.y,
        width: rect.width,
        height: rect.height + spacing,
      };
  }
}

function hasCollision(
  existingScreens: ScreenBounds[],
  candidate: Size & XYPosition,
) {
  return existingScreens.some((screen) => rectsOverlap(candidate, screen));
}

function getCollidingScreens(
  existingScreens: ScreenBounds[],
  candidate: Size & XYPosition,
) {
  return existingScreens.filter((screen) => rectsOverlap(candidate, screen));
}

function getDefaultScreenSize(
  screen: ScreenLike,
  flowGenType: GenType | null,
): Size {
  if (screen.type === 'component') {
    return {
      width: ESTIMATED_COMPONENT_SIZE,
      height: ESTIMATED_COMPONENT_SIZE,
    };
  }

  const fallbackType = screen.screenSize ?? flowGenType ?? 'web-html-css';
  return getNodeDimensions(fallbackType);
}

function getNodeSize(node: NodeLike | undefined, fallbackSize: Size): Size {
  const width = node?.measured?.width ?? node?.width;
  const height = node?.measured?.height ?? node?.height;

  if (typeof width === 'number' && typeof height === 'number') {
    return {width, height};
  }

  return fallbackSize;
}

function getFlowSizeFromScreenRect(args: {
  rect: Pick<DOMRect, 'left' | 'top' | 'right' | 'bottom'>;
  screenToFlowPosition: (position: XYPosition) => XYPosition;
}): Size {
  const topLeft = args.screenToFlowPosition({
    x: args.rect.left,
    y: args.rect.top,
  });
  const bottomRight = args.screenToFlowPosition({
    x: args.rect.right,
    y: args.rect.bottom,
  });

  return {
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y,
  };
}

function getScreenElementSize(
  screenId: string | undefined,
  screenToFlowPosition?: (position: XYPosition) => XYPosition,
): Size | null {
  if (!screenId) return null;

  const element = document.getElementById(`screen-id-${screenId}`);
  const rect = element?.getBoundingClientRect();

  if (!rect || rect.width === 0 || rect.height === 0) {
    return null;
  }

  if (!screenToFlowPosition) {
    return {
      width: rect.width,
      height: rect.height,
    };
  }

  return getFlowSizeFromScreenRect({
    rect,
    screenToFlowPosition,
  });
}

export function buildScreenBounds(args: {
  screens: ScreenLike[];
  flowGenType: GenType | null;
  getNode: (screenId: string) => NodeLike | undefined;
  screenToFlowPosition?: (position: XYPosition) => XYPosition;
}): ScreenBounds[] {
  const {screens, flowGenType, getNode, screenToFlowPosition} = args;

  return screens.flatMap((screen) => {
    const node = getNode(screen.id);
    const fallbackSize = getDefaultScreenSize(screen, flowGenType);
    const size =
      screen.type === 'screen'
        ? (getScreenElementSize(screen.id, screenToFlowPosition) ??
          getNodeSize(node, fallbackSize))
        : getNodeSize(node, fallbackSize);
    const x = node?.position?.x ?? screen.x;
    const y = node?.position?.y ?? screen.y;

    if (typeof x !== 'number' || typeof y !== 'number') {
      return [];
    }

    return [
      {
        id: screen.id,
        x,
        y,
        width: size.width,
        height: size.height,
        screenSize: node?.data?.screenSize ?? screen.screenSize,
        type: screen.type,
      },
    ];
  });
}

export function getViewportCenterInFlowCoordinates({
  screenToFlowPosition,
  viewportWidth,
  viewportHeight,
}: ViewportCenterArgs): XYPosition {
  const canvasRect = document
    .querySelector('.react-flow')
    ?.getBoundingClientRect();
  const sidebarRect = document
    .getElementById('flow-sidebar')
    ?.getBoundingClientRect();
  const resolvedViewportWidth = viewportWidth ?? window.innerWidth;
  const resolvedViewportHeight = viewportHeight ?? window.innerHeight;
  const sidebarOccupiedWidth =
    (sidebarRect?.left ?? 0) + (sidebarRect?.width ?? 248);
  const center = screenToFlowPosition({
    x:
      (canvasRect?.left ?? 0) +
      (canvasRect?.width ?? resolvedViewportWidth) / 2 +
      sidebarOccupiedWidth / 2,
    y:
      (canvasRect?.top ?? 0) +
      (canvasRect?.height ?? resolvedViewportHeight) / 2,
  });
  const offsetY = (canvasRect?.height ?? resolvedViewportHeight) * 0.05;

  return {
    x: center.x,
    y: center.y - offsetY,
  };
}

export function putInViewportCenter(
  viewportCenter: XYPosition,
  size: Size,
): XYPosition {
  return roundPosition({
    x: viewportCenter.x - size.width / 2,
    y: viewportCenter.y - size.height / 2,
  });
}

export function putAtDirection(
  existingScreens: ScreenBounds[],
  referenceScreen: ScreenBounds,
  size: Size,
  direction: Direction,
): XYPosition {
  let candidate: XYPosition;
  switch (direction) {
    case 'left':
      candidate = {
        x: referenceScreen.x - size.width - NODE_SPACING,
        y: referenceScreen.y,
      };
      break;
    case 'right':
      candidate = {
        x: referenceScreen.x + referenceScreen.width + NODE_SPACING,
        y: referenceScreen.y,
      };
      break;
    case 'top':
      candidate = {
        x: referenceScreen.x,
        y: referenceScreen.y - size.height - NODE_SPACING,
      };
      break;
    case 'bottom':
      candidate = {
        x: referenceScreen.x,
        y: referenceScreen.y + referenceScreen.height + NODE_SPACING,
      };
      break;
  }

  const maxAttempts = 200;
  let lastCandidate = roundPosition(candidate);

  for (let attempts = 0; attempts < maxAttempts; attempts++) {
    const roundedCandidate = roundPosition(candidate);
    const candidateRect = {
      ...roundedCandidate,
      ...size,
    };
    const candidateSpacingRect = inflateRectInDirection(
      candidateRect,
      NODE_SPACING,
      direction,
    );
    const collidingScreens = getCollidingScreens(
      existingScreens,
      candidateSpacingRect,
    );

    if (collidingScreens.length === 0) {
      return roundedCandidate;
    }

    lastCandidate = roundedCandidate;

    switch (direction) {
      case 'left':
        candidate = {
          x: Math.min(
            ...collidingScreens.map(
              (screen) => screen.x - size.width - NODE_SPACING,
            ),
          ),
          y: candidate.y,
        };
        break;
      case 'right':
        candidate = {
          x: Math.max(
            ...collidingScreens.map(
              (screen) => screen.x + screen.width + NODE_SPACING,
            ),
          ),
          y: candidate.y,
        };
        break;
      case 'top':
        candidate = {
          x: candidate.x,
          y: Math.min(
            ...collidingScreens.map(
              (screen) => screen.y - size.height - NODE_SPACING,
            ),
          ),
        };
        break;
      case 'bottom':
        candidate = {
          x: candidate.x,
          y: Math.max(
            ...collidingScreens.map(
              (screen) => screen.y + screen.height + NODE_SPACING,
            ),
          ),
        };
        break;
    }
  }

  return lastCandidate;
}

export function putInFree(
  existingScreens: ScreenBounds[],
  preferredPosition: XYPosition,
  size: Size,
): XYPosition {
  const roundedPreferred = roundPosition(preferredPosition);
  const preferredRect = {
    ...roundedPreferred,
    ...size,
  };
  const preferredSpacingRect = inflateRect(preferredRect, NODE_SPACING);

  if (!hasCollision(existingScreens, preferredSpacingRect)) {
    return roundedPreferred;
  }

  const candidateXs = new Set<number>([roundedPreferred.x]);
  const candidateYs = new Set<number>([roundedPreferred.y]);

  for (const screen of existingScreens) {
    candidateXs.add(screen.x - size.width - NODE_SPACING);
    candidateXs.add(screen.x + screen.width + NODE_SPACING);
    candidateYs.add(screen.y - size.height - NODE_SPACING);
    candidateYs.add(screen.y + screen.height + NODE_SPACING);
  }

  const candidates = Array.from(candidateXs).flatMap((x) =>
    Array.from(candidateYs).map((y) => roundPosition({x, y})),
  );

  candidates.sort((a, b) => {
    const distanceA = Math.hypot(
      a.x - preferredPosition.x,
      a.y - preferredPosition.y,
    );
    const distanceB = Math.hypot(
      b.x - preferredPosition.x,
      b.y - preferredPosition.y,
    );
    return distanceA - distanceB;
  });

  let lastCandidate = roundedPreferred;

  for (const candidate of candidates) {
    const candidateRect = {
      ...candidate,
      ...size,
    };
    const candidateSpacingRect = inflateRect(candidateRect, NODE_SPACING);
    lastCandidate = candidate;

    if (!hasCollision(existingScreens, candidateSpacingRect)) {
      return candidate;
    }
  }

  return lastCandidate;
}
